import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { usePageSEO } from "../../hooks/usePageSEO";
import { createWebSiteStructuredData, createBreadcrumbStructuredData, createOrganizationStructuredData } from "../../utils/structuredData";

export default function Home() {
  // SEO設定
  usePageSEO({
    title: 'DMM FX 評判・口コミ｜初心者向け完全ガイド【2025年最新】',
    description: 'DMM FXの評判・口コミを徹底調査。初心者でも安心して始められる理由、スプレッド比較、危険性の真実、口座開設のやり方まで完全解説。業界最狭水準のスプレッド、24時間サポート、最大50万円キャッシュバック。',
    keywords: 'DMM FX,評判,口コミ,初心者,危険,スプレッド,口座開設,やり方,使い方,メリット,デメリット,FX おすすめ 初心者,FX 口座 比較,FX 初心者 口座',
    structuredData: [
      createWebSiteStructuredData({
        name: 'DMM FX完全ガイド',
        description: 'DMM FXの評判・口コミから口座開設まで、初心者向けに徹底解説するサイト',
        url: '/',
      }),
      createOrganizationStructuredData({
        name: 'DMM FX情報局',
        description: 'DMM FXに関する正確で信頼できる情報を提供',
        url: '/',
      }),
      createBreadcrumbStructuredData([
        { name: 'ホーム', url: '/' },
      ]),
    ],
  });

  // 強化版CTAボタンコンポーネント
  const CTAButton = () => (
    <div className="text-center">
      <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-amber-400 to-orange-500 text-gray-900 px-3 md:px-4 py-1.5 md:py-2 rounded-full text-xs md:text-sm font-bold mb-3 md:mb-4 animate-pulse">
        <i className="ri-gift-line"></i>
        <span>👉 今だけ最大50万円キャッシュバック</span>
      </div>
      <div>
        <a 
          href="https://px.a8.net/svt/ejp?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" 
          rel="nofollow"
          className="inline-block bg-gradient-to-b from-orange-500 to-orange-600 text-white px-6 md:px-9 py-4 md:py-5 rounded-xl text-base md:text-2xl font-bold hover:scale-105 transition-all duration-200 whitespace-nowrap cursor-pointer shadow-[0_6px_18px_rgba(0,0,0,0.25)]"
        >
          <span className="hidden md:inline">👉 今だけ最大50万円キャッシュバック<br /></span>
          <span className="md:hidden">👉 最大50万円キャッシュバック<br /></span>
          無料でDMM FX口座を開設する
        </a>
        <img border="0" width="1" height="1" src="https://www13.a8.net/0.gif?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" alt="" />
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* ===== ファーストビュー ===== */}
      <section className="relative py-16 md:py-20 overflow-hidden min-h-[85vh] md:min-h-[80vh] flex items-center">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/e14df20d230b19ddf110233f834a5cef.jpg"
            alt="FX取引イメージ"
            className="w-full h-full object-cover object-center"
            loading="eager"
            decoding="async"
            fetchPriority="high"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/55 to-black/75"></div>
        <div className="absolute inset-0 bg-gradient-to-br from-teal-900/30 via-transparent to-amber-900/20"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-12 md:pt-16">
          <div className="text-center max-w-4xl mx-auto">
            <p className="text-white/90 text-sm md:text-lg lg:text-xl font-medium mb-3 md:mb-4">初心者でも安心して始められる</p>
            
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-5 md:mb-6 leading-tight">
              <span className="bg-gradient-to-r from-teal-300 via-emerald-300 to-teal-300 bg-clip-text text-transparent" style={{ WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                DMM FX
              </span>
              <span className="text-white">完全ガイド</span>
            </h1>
            
            {/* 数字で訴求 */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4 mb-6 md:mb-8 max-w-3xl mx-auto">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg md:rounded-xl p-3 md:p-4 border border-white/20">
                <div className="text-xl md:text-2xl lg:text-3xl font-bold text-amber-400">90万+</div>
                <div className="text-white/80 text-xs md:text-sm">口座数</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg md:rounded-xl p-3 md:p-4 border border-white/20">
                <div className="text-xl md:text-2xl lg:text-3xl font-bold text-amber-400">0.2<span className="text-sm md:text-lg">銭</span></div>
                <div className="text-white/80 text-xs md:text-sm">ドル円スプレッド</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg md:rounded-xl p-3 md:p-4 border border-white/20">
                <div className="text-xl md:text-2xl lg:text-3xl font-bold text-amber-400">最短5<span className="text-sm md:text-lg">分</span></div>
                <div className="text-white/80 text-xs md:text-sm">口座開設</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg md:rounded-xl p-3 md:p-4 border border-white/20">
                <div className="text-xl md:text-2xl lg:text-3xl font-bold text-amber-400">24<span className="text-sm md:text-lg">時間</span></div>
                <div className="text-white/80 text-xs md:text-sm">サポート対応</div>
              </div>
            </div>
            
            {/* ベネフィットリスト */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-2 md:gap-4 lg:gap-8 mb-8 md:mb-10">
              <div className="flex items-center space-x-2 text-white">
                <i className="ri-check-line text-teal-300 text-lg md:text-xl"></i>
                <span className="text-sm md:text-base font-medium">スプレッドが業界最狭水準</span>
              </div>
              <div className="flex items-center space-x-2 text-white">
                <i className="ri-check-line text-teal-300 text-lg md:text-xl"></i>
                <span className="text-sm md:text-base font-medium">スマホ5分で口座開設</span>
              </div>
              <div className="flex items-center space-x-2 text-white">
                <i className="ri-check-line text-teal-300 text-lg md:text-xl"></i>
                <span className="text-sm md:text-base font-medium">最大50万円キャッシュバック</span>
              </div>
            </div>
            
            {/* CTA 1: ファーストビュー */}
            <CTAButton />
          </div>
        </div>
      </section>

      {/* ===== 初心者におすすめのFX口座（トップLPセクション） ===== */}
      <section className="py-12 md:py-16 bg-gradient-to-br from-slate-50 via-white to-teal-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl lg:text-5xl font-bold text-gray-900 mb-4 md:mb-6">
            初心者におすすめのFX口座
          </h2>
          <p className="text-base md:text-lg lg:text-xl text-gray-700 mb-8 md:mb-10 leading-relaxed">
            FX初心者に人気の国内口座を比較した結果、<br className="hidden md:block" />
            <strong className="text-teal-600 text-lg md:text-xl lg:text-2xl">DMM FXが最もおすすめ</strong>です。
          </p>

          {/* DMM FXカード */}
          <div className="bg-white rounded-xl md:rounded-2xl p-6 md:p-8 lg:p-10 max-w-xl mx-auto shadow-[0_4px_20px_rgba(0,0,0,0.1)] border border-gray-100">
            <div className="flex items-center justify-center space-x-3 mb-5 md:mb-6">
              <div className="bg-gradient-to-br from-teal-500 to-emerald-600 text-white px-2.5 md:px-3 py-1 rounded-full text-xs font-bold">
                No.1
              </div>
              <h3 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900">DMM FX</h3>
            </div>
            
            <ul className="text-left space-y-2.5 md:space-y-3 mb-6 md:mb-8">
              <li className="flex items-center space-x-3">
                <div className="w-5 h-5 md:w-6 md:h-6 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-teal-600 text-xs md:text-sm"></i>
                </div>
                <span className="text-sm md:text-base text-gray-700">口座数 国内トップクラス</span>
              </li>
              <li className="flex items-center space-x-3">
                <div className="w-5 h-5 md:w-6 md:h-6 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-teal-600 text-xs md:text-sm"></i>
                </div>
                <span className="text-sm md:text-base text-gray-700">スマホ完結で口座開設</span>
              </li>
              <li className="flex items-center space-x-3">
                <div className="w-5 h-5 md:w-6 md:h-6 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-teal-600 text-xs md:text-sm"></i>
                </div>
                <span className="text-sm md:text-base text-gray-700">スプレッド最狭水準（0.2銭）</span>
              </li>
              <li className="flex items-center space-x-3">
                <div className="w-5 h-5 md:w-6 md:h-6 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-teal-600 text-xs md:text-sm"></i>
                </div>
                <span className="text-sm md:text-base text-gray-700">24時間サポート充実</span>
              </li>
            </ul>

            <a 
              href="https://px.a8.net/svt/ejp?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" 
              rel="nofollow"
              className="inline-block bg-gradient-to-b from-red-500 to-red-600 text-white px-6 md:px-8 py-3 md:py-4 rounded-lg text-base md:text-lg font-bold hover:scale-105 transition-all duration-200 whitespace-nowrap cursor-pointer shadow-[0_4px_12px_rgba(239,68,68,0.4)]"
            >
              無料で口座開設する
            </a>
            <img border="0" width="1" height="1" src="https://www13.a8.net/0.gif?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" alt="" style={{ display: 'none' }} />
          </div>

          {/* 詳しく知りたい方へのリンク */}
          <div className="mt-6 md:mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4">
            <Link
              to="/reviews"
              className="inline-flex items-center space-x-2 text-teal-600 font-semibold hover:text-teal-700 cursor-pointer bg-white px-4 py-2 rounded-lg border border-teal-200 hover:border-teal-400 transition-all text-sm md:text-base"
            >
              <i className="ri-star-line"></i>
              <span>評判・口コミを見る</span>
            </Link>
            <Link
              to="/account-opening"
              className="inline-flex items-center space-x-2 text-teal-600 font-semibold hover:text-teal-700 cursor-pointer bg-white px-4 py-2 rounded-lg border border-teal-200 hover:border-teal-400 transition-all text-sm md:text-base"
            >
              <i className="ri-file-list-3-line"></i>
              <span>口座開設の流れ</span>
            </Link>
            <Link
              to="/comparison"
              className="inline-flex items-center space-x-2 text-teal-600 font-semibold hover:text-teal-700 cursor-pointer bg-white px-4 py-2 rounded-lg border border-teal-200 hover:border-teal-400 transition-all text-sm md:text-base"
            >
              <i className="ri-scales-line"></i>
              <span>他社と比較</span>
            </Link>
          </div>
        </div>
      </section>

      {/* 選ばれる理由セクション */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 md:mb-4">
              DMM FXが初心者に選ばれる理由
            </h2>
            <p className="text-sm md:text-base text-gray-600">5つの安心ポイント</p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 md:gap-6 mb-8 md:mb-12">
            <Link
              to="/spread"
              className="group bg-gradient-to-br from-white to-gray-50 rounded-lg md:rounded-xl p-4 md:p-6 border border-gray-200 hover:shadow-lg transition-shadow cursor-pointer"
            >
              <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg md:rounded-xl flex items-center justify-center mb-3 md:mb-4 group-hover:scale-110 transition-transform">
                <i className="ri-funds-line text-white text-xl md:text-2xl"></i>
              </div>
              <h3 className="text-sm md:text-lg font-bold text-gray-900 mb-1 md:mb-2 group-hover:text-teal-600 transition-colors">
                スプレッドが業界最狭水準
              </h3>
              <p className="text-xs md:text-sm text-gray-600 mb-2 md:mb-3 hidden md:block">米ドル/円0.2銭で取引コストを最小限に</p>
              <div className="flex items-center text-teal-600 text-xs md:text-sm font-semibold">
                <span>詳しく見る</span>
                <i className="ri-arrow-right-line ml-1"></i>
              </div>
            </Link>

            <Link
              to="/account-opening"
              className="group bg-gradient-to-br from-white to-gray-50 rounded-lg md:rounded-xl p-4 md:p-6 border border-gray-200 hover:shadow-lg transition-shadow cursor-pointer"
            >
              <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg md:rounded-xl flex items-center justify-center mb-3 md:mb-4 group-hover:scale-110 transition-transform">
                <i className="ri-smartphone-line text-white text-xl md:text-2xl"></i>
              </div>
              <h3 className="text-sm md:text-lg font-bold text-gray-900 mb-1 md:mb-2 group-hover:text-teal-600 transition-colors">
                口座開設がスマホで完結
              </h3>
              <p className="text-xs md:text-sm text-gray-600 mb-2 md:mb-3 hidden md:block">最短5分で申込、即日取引可能</p>
              <div className="flex items-center text-teal-600 text-xs md:text-sm font-semibold">
                <span>詳しく見る</span>
                <i className="ri-arrow-right-line ml-1"></i>
              </div>
            </Link>

            <Link
              to="/app-guide"
              className="group bg-gradient-to-br from-white to-gray-50 rounded-lg md:rounded-xl p-4 md:p-6 border border-gray-200 hover:shadow-lg transition-shadow cursor-pointer"
            >
              <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg md:rounded-xl flex items-center justify-center mb-3 md:mb-4 group-hover:scale-110 transition-transform">
                <i className="ri-apps-line text-white text-xl md:text-2xl"></i>
              </div>
              <h3 className="text-sm md:text-lg font-bold text-gray-900 mb-1 md:mb-2 group-hover:text-teal-600 transition-colors">
                アプリが使いやすい
              </h3>
              <p className="text-xs md:text-sm text-gray-600 mb-2 md:mb-3 hidden md:block">直感的な操作で初心者でも安心</p>
              <div className="flex items-center text-teal-600 text-xs md:text-sm font-semibold">
                <span>詳しく見る</span>
                <i className="ri-arrow-right-line ml-1"></i>
              </div>
            </Link>

            <Link
              to="/support"
              className="group bg-gradient-to-br from-white to-gray-50 rounded-lg md:rounded-xl p-4 md:p-6 border border-gray-200 hover:shadow-lg transition-shadow cursor-pointer"
            >
              <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg md:rounded-xl flex items-center justify-center mb-3 md:mb-4 group-hover:scale-110 transition-transform">
                <i className="ri-customer-service-2-line text-white text-xl md:text-2xl"></i>
              </div>
              <h3 className="text-sm md:text-lg font-bold text-gray-900 mb-1 md:mb-2 group-hover:text-teal-600 transition-colors">
                24時間サポート
              </h3>
              <p className="text-xs md:text-sm text-gray-600 mb-2 md:mb-3 hidden md:block">電話・メール・LINEで安心</p>
              <div className="flex items-center text-teal-600 text-xs md:text-sm font-semibold">
                <span>詳しく見る</span>
                <i className="ri-arrow-right-line ml-1"></i>
              </div>
            </Link>

            <div className="col-span-2 lg:col-span-1 bg-gradient-to-br from-white to-gray-50 rounded-lg md:rounded-xl p-4 md:p-6 border border-gray-200">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg md:rounded-xl flex items-center justify-center mb-3 md:mb-4">
                <i className="ri-shield-check-line text-white text-xl md:text-2xl"></i>
              </div>
              <h3 className="text-sm md:text-lg font-bold text-gray-900 mb-1 md:mb-2">大手DMMグループの安心感</h3>
              <p className="text-xs md:text-sm text-gray-600 mb-2 md:mb-3">
                信頼の大手企業だから安心して取引できます
              </p>
            </div>
          </div>

          {/* CTA 2: 選ばれる理由の後 */}
          <CTAButton />
        </div>
      </section>

      {/* ===== DMM FX完全ガイド（ハブブロック） ===== */}
      <section className="py-12 md:py-16 bg-gradient-to-br from-teal-50 via-emerald-50 to-teal-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-12">
            <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-teal-500 to-emerald-600 text-white px-3 md:px-4 py-1.5 md:py-2 rounded-full mb-3 md:mb-4 text-xs md:text-sm font-bold">
              <i className="ri-book-open-line"></i>
              <span>DMM FX完全ガイド</span>
            </div>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 md:mb-4">
              DMM FXのすべてがわかる
            </h2>
            <p className="text-sm md:text-base text-gray-600">評判から口座開設まで、知りたい情報をすべて網羅</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {/* 評判・口コミ（ハブ記事） */}
            <Link
              to="/reviews"
              className="group bg-white rounded-xl p-5 md:p-8 border-2 border-teal-500 hover:shadow-2xl transition-all cursor-pointer relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 bg-gradient-to-br from-teal-500 to-emerald-600 text-white px-2 md:px-3 py-1 text-xs font-bold rounded-bl-lg">
                人気No.1
              </div>
              <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-xl flex items-center justify-center mb-3 md:mb-4 group-hover:scale-110 transition-transform">
                <i className="ri-star-line text-white text-2xl md:text-3xl"></i>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 md:mb-3 group-hover:text-teal-600 transition-colors">
                評判・口コミ
              </h3>
              <p className="text-xs md:text-sm text-gray-600 mb-3 md:mb-4 leading-relaxed">
                実際の利用者1,247件の口コミを徹底分析。良い評判・悪い評判の両方を正直に紹介します。
              </p>
              <div className="flex items-center text-teal-600 text-xs md:text-sm font-semibold">
                <span>詳しく見る</span>
                <i className="ri-arrow-right-line ml-1 group-hover:translate-x-1 transition-transform"></i>
              </div>
            </Link>

            {/* 口座開設のやり方 */}
            <Link
              to="/account-opening"
              className="group bg-white rounded-xl p-5 md:p-8 border border-gray-200 hover:shadow-2xl hover:border-teal-500 transition-all cursor-pointer"
            >
              <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-xl flex items-center justify-center mb-3 md:mb-4 group-hover:scale-110 transition-transform">
                <i className="ri-file-list-3-line text-white text-2xl md:text-3xl"></i>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 md:mb-3 group-hover:text-teal-600 transition-colors">
                口座開設のやり方
              </h3>
              <p className="text-xs md:text-sm text-gray-600 mb-3 md:mb-4 leading-relaxed">
                スマホで最短5分。画像付きで口座開設の手順を詳しく解説します。
              </p>
              <div className="flex items-center text-teal-600 text-xs md:text-sm font-semibold">
                <span>詳しく見る</span>
                <i className="ri-arrow-right-line ml-1 group-hover:translate-x-1 transition-transform"></i>
              </div>
            </Link>

            {/* キャンペーン情報 */}
            <Link
              to="/campaign"
              className="group bg-white rounded-xl p-5 md:p-8 border border-gray-200 hover:shadow-2xl hover:border-teal-500 transition-all cursor-pointer"
            >
              <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-xl flex items-center justify-center mb-3 md:mb-4 group-hover:scale-110 transition-transform">
                <i className="ri-gift-line text-white text-2xl md:text-3xl"></i>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 md:mb-3 group-hover:text-teal-600 transition-colors">
                キャンペーン情報
              </h3>
              <p className="text-xs md:text-sm text-gray-600 mb-3 md:mb-4 leading-relaxed">
                最大50万円キャッシュバックの詳細と受け取り方法を解説します。
              </p>
              <div className="flex items-center text-teal-600 text-xs md:text-sm font-semibold">
                <span>詳しく見る</span>
                <i className="ri-arrow-right-line ml-1 group-hover:translate-x-1 transition-transform"></i>
              </div>
            </Link>

            {/* メリット・デメリット */}
            <Link
              to="/beginner-guide"
              className="group bg-white rounded-xl p-5 md:p-8 border border-gray-200 hover:shadow-2xl hover:border-teal-500 transition-all cursor-pointer"
            >
              <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-xl flex items-center justify-center mb-3 md:mb-4 group-hover:scale-110 transition-transform">
                <i className="ri-scales-3-line text-white text-2xl md:text-3xl"></i>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 md:mb-3 group-hover:text-teal-600 transition-colors">
                メリット・デメリット
              </h3>
              <p className="text-xs md:text-sm text-gray-600 mb-3 md:mb-4 leading-relaxed">
                DMM FXの良い点・悪い点を正直に比較。あなたに合うか判断できます。
              </p>
              <div className="flex items-center text-teal-600 text-xs md:text-sm font-semibold">
                <span>詳しく見る</span>
                <i className="ri-arrow-right-line ml-1 group-hover:translate-x-1 transition-transform"></i>
              </div>
            </Link>

            {/* 安全性・危険性 */}
            <Link
              to="/safety"
              className="group bg-white rounded-xl p-5 md:p-8 border border-gray-200 hover:shadow-2xl hover:border-teal-500 transition-all cursor-pointer"
            >
              <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-xl flex items-center justify-center mb-3 md:mb-4 group-hover:scale-110 transition-transform">
                <i className="ri-shield-check-line text-white text-2xl md:text-3xl"></i>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 md:mb-3 group-hover:text-teal-600 transition-colors">
                安全性・危険性
              </h3>
              <p className="text-xs md:text-sm text-gray-600 mb-3 md:mb-4 leading-relaxed">
                FXは危険？金融庁登録・信託保全など安全性を徹底解説します。
              </p>
              <div className="flex items-center text-teal-600 text-xs md:text-sm font-semibold">
                <span>詳しく見る</span>
                <i className="ri-arrow-right-line ml-1 group-hover:translate-x-1 transition-transform"></i>
              </div>
            </Link>

            {/* スプレッド比較 */}
            <Link
              to="/spread"
              className="group bg-white rounded-xl p-5 md:p-8 border border-gray-200 hover:shadow-2xl hover:border-teal-500 transition-all cursor-pointer"
            >
              <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-xl flex items-center justify-center mb-3 md:mb-4 group-hover:scale-110 transition-transform">
                <i className="ri-funds-line text-white text-2xl md:text-3xl"></i>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 md:mb-3 group-hover:text-teal-600 transition-colors">
                スプレッド比較
              </h3>
              <p className="text-xs md:text-sm text-gray-600 mb-3 md:mb-4 leading-relaxed">
                取引コストを他社と徹底比較。業界最狭水準の理由を解説します。
              </p>
              <div className="flex items-center text-teal-600 text-xs md:text-sm font-semibold">
                <span>詳しく見る</span>
                <i className="ri-arrow-right-line ml-1 group-hover:translate-x-1 transition-transform"></i>
              </div>
            </Link>
          </div>

          {/* ハブブロック下のCTA */}
          <div className="mt-8 md:mt-12 text-center">
            <p className="text-sm md:text-lg text-gray-600 mb-4 md:mb-6">
              まずは評判・口コミをチェックして、DMM FXがあなたに合うか確認しましょう
            </p>
            <Link
              to="/reviews"
              className="inline-flex items-center space-x-2 bg-gradient-to-r from-teal-500 to-emerald-600 text-white px-6 md:px-8 py-3 md:py-4 rounded-lg text-base md:text-lg font-bold hover:shadow-xl hover:scale-105 transition-all cursor-pointer"
            >
              <i className="ri-star-line text-lg md:text-xl"></i>
              <span>評判・口コミを見る</span>
              <i className="ri-arrow-right-line text-xl md:text-2xl"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* ===== 比較ブロック（CV爆上げ） ===== */}
      <section className="py-12 md:py-16 bg-gradient-to-br from-teal-50 to-emerald-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 md:mb-4">
              DMM FXが他社より優れている理由
            </h2>
            <p className="text-sm md:text-base text-gray-600">なぜDMM FXが選ばれるのか一目でわかる</p>
          </div>

          <div className="bg-white rounded-xl md:rounded-2xl shadow-xl overflow-hidden border border-gray-200">
            <div className="overflow-x-auto">
              <table className="w-full text-xs md:text-base" style={{ borderCollapse: 'collapse' }}>
                <thead>
                  <tr className="bg-gray-50">
                    <th className="p-2.5 md:p-3 border border-gray-200 text-left font-bold">比較項目</th>
                    <th className="p-2.5 md:p-3 border border-gray-200 text-center font-bold">DMM FX</th>
                    <th className="p-2.5 md:p-3 border border-gray-200 text-center font-bold">他社FX</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="p-2.5 md:p-3 border border-gray-200">スプレッド</td>
                    <td className="p-2.5 md:p-3 border border-gray-200 text-orange-500 font-bold text-center">0.2銭<span className="hidden md:inline">（業界最狭水準）</span></td>
                    <td className="p-2.5 md:p-3 border border-gray-200 text-center">0.4〜0.6銭</td>
                  </tr>
                  <tr>
                    <td className="p-2.5 md:p-3 border border-gray-200">口座開設</td>
                    <td className="p-2.5 md:p-3 border border-gray-200 text-orange-500 font-bold text-center">スマホ5分<span className="hidden md:inline">・最短当日</span></td>
                    <td className="p-2.5 md:p-3 border border-gray-200 text-center">1〜2日</td>
                  </tr>
                  <tr>
                    <td className="p-2.5 md:p-3 border border-gray-200">取引アプリ</td>
                    <td className="p-2.5 md:p-3 border border-gray-200 text-orange-500 font-bold text-center">直感操作<span className="hidden md:inline">で初心者向け</span></td>
                    <td className="p-2.5 md:p-3 border border-gray-200 text-center">やや複雑</td>
                  </tr>
                  <tr>
                    <td className="p-2.5 md:p-3 border border-gray-200">サポート</td>
                    <td className="p-2.5 md:p-3 border border-gray-200 text-orange-500 font-bold text-center">24時間対応</td>
                    <td className="p-2.5 md:p-3 border border-gray-200 text-center">平日のみ</td>
                  </tr>
                  <tr>
                    <td className="p-2.5 md:p-3 border border-gray-200">キャッシュバック</td>
                    <td className="p-2.5 md:p-3 border border-gray-200 text-orange-500 font-bold text-center">最大50万円</td>
                    <td className="p-2.5 md:p-3 border border-gray-200 text-center">数万円</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* 比較後のCTA */}
          <div className="mt-8 md:mt-10">
            <CTAButton />
          </div>
        </div>
      </section>

      {/* 口コミセクション */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 md:mb-4">
              利用者の口コミ
            </h2>
            <div className="flex items-center justify-center space-x-3 md:space-x-4 mb-2">
              <div className="flex items-center space-x-0.5 md:space-x-1">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className={`${i < 4 ? 'ri-star-fill text-yellow-500' : 'ri-star-line text-gray-300'} text-lg md:text-xl`}></i>
                ))}
              </div>
              <span className="text-xl md:text-2xl font-bold text-gray-900">4.2</span>
            </div>
            <p className="text-sm md:text-base text-gray-600">1,247件の口コミ</p>
          </div>

          <div className="grid md:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8">
            {/* 口コミカード */}
            <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-4 md:p-6 border border-gray-200 shadow-sm">
              <div className="flex items-center space-x-0.5 md:space-x-1 mb-2 md:mb-3">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-yellow-500 text-sm md:text-base"></i>
                ))}
              </div>
              <p className="text-gray-700 mb-3 md:mb-4 text-xs md:text-sm leading-relaxed">
                スマホだけで簡単に始められました。FX初心者でしたが、デモ口座で練習してから本番に移行できたので安心でした。
              </p>
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>田中さん（32歳・FX歴2年）</span>
                <span>2024年12月</span>
              </div>
            </div>

            <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-4 md:p-6 border border-gray-200 shadow-sm">
              <div className="flex items-center space-x-0.5 md:space-x-1 mb-2 md:mb-3">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-yellow-500 text-sm md:text-base"></i>
                ))}
              </div>
              <p className="text-gray-700 mb-3 md:mb-4 text-xs md:text-sm leading-relaxed">
                スプレッドが本当に狭いです。ドル円0.2銭は他社と比較しても業界最狭水準。デイトレードをしているので、コストを抑えられるのが大きなメリットです。
              </p>
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>佐藤さん（28歳・FX歴6ヶ月）</span>
                <span>2024年11月</span>
              </div>
            </div>

            <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-4 md:p-6 border border-gray-200 shadow-sm">
              <div className="flex items-center space-x-0.5 md:space-x-1 mb-2 md:mb-3">
                {[...Array(4)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-yellow-500 text-sm md:text-base"></i>
                ))}
                <i className="ri-star-line text-gray-300 text-sm md:text-base"></i>
              </div>
              <p className="text-gray-700 mb-3 md:mb-4 text-xs md:text-sm leading-relaxed">
                サポートが丁寧で信頼できます。24時間対応なので、深夜に取引していて疑問が出てきた時もすぐに問い合わせできて安心です。
              </p>
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>山田さん（45歳・FX歴5年）</span>
                <span>2024年12月</span>
              </div>
            </div>
          </div>

          {/* もっと口コミを見る */}
          <div className="text-center">
            <Link
              to="/reviews"
              className="inline-flex items-center space-x-2 bg-white text-teal-600 px-5 md:px-6 py-2.5 md:py-3 rounded-lg font-semibold border-2 border-teal-600 hover:bg-teal-50 transition-all cursor-pointer text-sm md:text-base"
            >
              <span>もっと口コミを見る</span>
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          {/* CTA 3: 口コミの後 */}
          <div className="mt-8 md:mt-10">
            <CTAButton />
          </div>
        </div>
      </section>

      {/* ===== 不安解消ブロック ===== */}
      <section className="py-12 md:py-16 bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 md:mb-4">
              FXは危険？初心者の不安を解消
            </h2>
            <p className="text-sm md:text-base text-gray-600">よくある不安にお答えします</p>
          </div>

          <div className="grid md:grid-cols-3 gap-4 md:gap-6 mb-8 md:mb-10">
            <div className="bg-white rounded-xl p-4 md:p-6 border border-gray-200 shadow-sm">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-teal-100 to-emerald-100 rounded-full flex items-center justify-center mb-3 md:mb-4">
                <i className="ri-question-line text-xl md:text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-base md:text-lg font-bold text-gray-900 mb-2 md:mb-3">FXは危険じゃないの？</h3>
              <div className="space-y-1.5 md:space-y-2">
                <div className="flex items-start space-x-2">
                  <i className="ri-check-line text-teal-500 mt-0.5 text-sm md:text-base"></i>
                  <p className="text-xs md:text-sm text-gray-600">国内業者は金融庁登録で安全</p>
                </div>
                <div className="flex items-start space-x-2">
                  <i className="ri-check-line text-teal-500 mt-0.5 text-sm md:text-base"></i>
                  <p className="text-xs md:text-sm text-gray-600">全額信託保全で資産を保護</p>
                </div>
                <div className="flex items-start space-x-2">
                  <i className="ri-check-line text-teal-500 mt-0.5 text-sm md:text-base"></i>
                  <p className="text-xs md:text-sm text-gray-600">レバレッジ管理で安心取引</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-4 md:p-6 border border-gray-200 shadow-sm">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-teal-100 to-emerald-100 rounded-full flex items-center justify-center mb-3 md:mb-4">
                <i className="ri-money-dollar-circle-line text-xl md:text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-base md:text-lg font-bold text-gray-900 mb-2 md:mb-3">いくらから始められる？</h3>
              <div className="space-y-1.5 md:space-y-2">
                <div className="flex items-start space-x-2">
                  <i className="ri-check-line text-teal-500 mt-0.5 text-sm md:text-base"></i>
                  <p className="text-xs md:text-sm text-gray-600">最低約5,000円から取引可能</p>
                </div>
                <div className="flex items-start space-x-2">
                  <i className="ri-check-line text-teal-500 mt-0.5 text-sm md:text-base"></i>
                  <p className="text-xs md:text-sm text-gray-600">少額から始めてリスク管理</p>
                </div>
                <div className="flex items-start space-x-2">
                  <i className="ri-check-line text-teal-500 mt-0.5 text-sm md:text-base"></i>
                  <p className="text-xs md:text-sm text-gray-600">デモ口座で無料練習OK</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-4 md:p-6 border border-gray-200 shadow-sm">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-teal-100 to-emerald-100 rounded-full flex items-center justify-center mb-3 md:mb-4">
                <i className="ri-user-star-line text-xl md:text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-base md:text-lg font-bold text-gray-900 mb-2 md:mb-3">初心者でも大丈夫？</h3>
              <div className="space-y-1.5 md:space-y-2">
                <div className="flex items-start space-x-2">
                  <i className="ri-check-line text-teal-500 mt-0.5 text-sm md:text-base"></i>
                  <p className="text-xs md:text-sm text-gray-600">24時間サポートで安心</p>
                </div>
                <div className="flex items-start space-x-2">
                  <i className="ri-check-line text-teal-500 mt-0.5 text-sm md:text-base"></i>
                  <p className="text-xs md:text-sm text-gray-600">充実の学習コンテンツ</p>
                </div>
                <div className="flex items-start space-x-2">
                  <i className="ri-check-line text-teal-500 mt-0.5 text-sm md:text-base"></i>
                  <p className="text-xs md:text-sm text-gray-600">直感的なアプリで簡単操作</p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center">
            <Link
              to="/safety"
              className="inline-flex items-center space-x-2 text-teal-600 font-semibold hover:text-teal-700 cursor-pointer text-sm md:text-base"
            >
              <span>FXの安全性について詳しく見る</span>
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* ===== 体験ストーリー ===== */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-xl md:rounded-2xl p-5 md:p-8 lg:p-12 border border-teal-100">
            <div className="flex flex-col md:flex-row items-center gap-5 md:gap-8">
              <div className="w-20 h-20 md:w-24 md:h-24 lg:w-32 lg:h-32 rounded-full overflow-hidden flex-shrink-0 border-4 border-white shadow-lg">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20Japanese%20woman%20in%20her%20late%20twenties%20smiling%20confidently%2C%20wearing%20casual%20business%20attire%2C%20clean%20white%20background%2C%20portrait%20photography%20style%2C%20warm%20natural%20lighting%2C%20friendly%20approachable%20expression%2C%20high%20quality%20headshot&width=200&height=200&seq=user-portrait-001&orientation=squarish"
                  alt="利用者の声"
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="flex-1 text-center md:text-left">
                <div className="flex items-center justify-center md:justify-start space-x-0.5 md:space-x-1 mb-2 md:mb-3">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-500 text-base md:text-lg"></i>
                  ))}
                </div>
                <p className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900 mb-3 md:mb-4 leading-relaxed">
                  「FX初心者だった私でも<br className="hidden md:block" />
                  <span className="text-teal-600">スマホだけで10分</span>で始められました」
                </p>
                <p className="text-xs md:text-sm lg:text-base text-gray-600 mb-3 md:mb-4 leading-relaxed">
                  投資は難しそうと思っていましたが、DMM FXは口座開設から取引開始まで本当に簡単でした。
                  デモ口座で練習してから始められたので、不安なく本番取引に移行できました。
                  今では毎日の取引が楽しみです。
                </p>
                <div className="text-xs md:text-sm text-gray-500">
                  鈴木さん（29歳・会社員）FX歴8ヶ月
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center mt-8 md:mt-10">
            <p className="text-sm md:text-base text-gray-600 mb-3 md:mb-4">あなたも今日からFXを始めてみませんか？</p>
            <a 
              href="https://px.a8.net/svt/ejp?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" 
              rel="nofollow"
              className="inline-flex items-center space-x-2 md:space-x-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 md:px-10 py-4 md:py-5 rounded-lg text-base md:text-xl font-bold hover:from-orange-600 hover:to-orange-700 hover:scale-105 hover:-translate-y-1 transition-all duration-300 whitespace-nowrap cursor-pointer shadow-[0_8px_20px_rgba(249,115,22,0.4)] hover:shadow-[0_12px_28px_rgba(249,115,22,0.5)]"
            >
              <span>無料で口座開設する</span>
              <i className="ri-arrow-right-line text-xl md:text-2xl"></i>
            </a>
            <img border="0" width="1" height="1" src="https://www13.a8.net/0.gif?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" alt="" style={{ display: 'none' }} />
          </div>
        </div>
      </section>

      {/* メリットセクション */}
      <section className="py-12 md:py-16 bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 md:mb-4">
              DMM FXのメリット
            </h2>
            <p className="text-sm md:text-base text-gray-600">初心者でも安心して始められる3つの理由</p>
          </div>

          <div className="grid md:grid-cols-3 gap-4 md:gap-8">
            <div className="text-center bg-white rounded-xl p-5 md:p-8 border border-gray-200 shadow-sm">
              <div className="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6">
                <i className="ri-money-dollar-circle-line text-3xl md:text-4xl text-white"></i>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">低コスト</h3>
              <p className="text-xs md:text-sm lg:text-base text-gray-600 leading-relaxed">
                業界最狭水準のスプレッドで取引コストを最小限に。米ドル/円0.2銭、ユーロ/円0.4銭など、他社と比較しても圧倒的に安い。
              </p>
            </div>

            <div className="text-center bg-white rounded-xl p-5 md:p-8 border border-gray-200 shadow-sm">
              <div className="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6">
                <i className="ri-smartphone-line text-3xl md:text-4xl text-white"></i>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">使いやすい</h3>
              <p className="text-xs md:text-sm lg:text-base text-gray-600 leading-relaxed">
                直感的な操作で初心者でもすぐに使いこなせる。スマホアプリは高機能で、外出先でもサクサク取引できる。
              </p>
            </div>

            <div className="text-center bg-white rounded-xl p-5 md:p-8 border border-gray-200 shadow-sm">
              <div className="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6">
                <i className="ri-user-star-line text-3xl md:text-4xl text-white"></i>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">初心者向け</h3>
              <p className="text-xs md:text-sm lg:text-base text-gray-600 leading-relaxed">
                24時間サポート、デモ口座、充実の学習コンテンツなど、初心者が安心して始められる環境が整っている。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 口座開設の流れセクション */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 md:mb-4">
              口座開設は3ステップ
            </h2>
            <p className="text-sm md:text-base text-gray-600">最短5分で申込完了、即日取引可能</p>
          </div>

          <div className="grid md:grid-cols-3 gap-4 md:gap-8 mb-8 md:mb-12">
            <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-5 md:p-8 text-center border border-gray-200 shadow-sm">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6">
                <span className="text-xl md:text-2xl font-bold text-white">1</span>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 md:mb-3">公式サイトから申込</h3>
              <p className="text-xs md:text-sm lg:text-base text-gray-600">
                スマホ・PCから最短5分で申込完了。本人確認書類とマイナンバーを準備。
              </p>
            </div>

            <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-5 md:p-8 text-center border border-gray-200 shadow-sm">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6">
                <span className="text-xl md:text-2xl font-bold text-white">2</span>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 md:mb-3">スマホで本人確認</h3>
              <p className="text-xs md:text-sm lg:text-base text-gray-600">
                スマホで本人確認書類を撮影して送信。最短即日で審査完了。
              </p>
            </div>

            <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-5 md:p-8 text-center border border-gray-200 shadow-sm">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6">
                <span className="text-xl md:text-2xl font-bold text-white">3</span>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 md:mb-3">最短当日取引開始</h3>
              <p className="text-xs md:text-sm lg:text-base text-gray-600">
                ログイン情報が届いたら入金して取引開始。デモ口座で練習もOK。
              </p>
            </div>
          </div>

          {/* 詳しい手順へのリンク */}
          <div className="text-center mb-6 md:mb-8">
            <Link
              to="/account-opening"
              className="inline-flex items-center space-x-2 text-teal-600 font-semibold hover:text-teal-700 cursor-pointer text-sm md:text-base"
            >
              <span>口座開設の詳しい手順を見る</span>
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          {/* 最終CTA */}
          <div className="text-center bg-gradient-to-br from-teal-600 to-emerald-600 rounded-xl md:rounded-2xl p-6 md:p-10">
            <h3 className="text-xl md:text-2xl lg:text-3xl font-bold text-white mb-3 md:mb-4">
              今すぐDMM FXを始めよう
            </h3>
            <div className="inline-flex items-center space-x-2 bg-white/20 text-white px-3 md:px-4 py-1.5 md:py-2 rounded-full mb-4 md:mb-6 text-xs md:text-sm font-bold">
              <i className="ri-gift-line"></i>
              <span>今なら最大50万円キャッシュバック実施中</span>
            </div>
            <div className="block">
              <a 
                href="https://px.a8.net/svt/ejp?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" 
                rel="nofollow"
                className="inline-flex items-center space-x-2 md:space-x-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-8 md:px-12 py-4 md:py-6 rounded-lg text-base md:text-xl font-bold hover:from-orange-600 hover:to-orange-700 hover:scale-105 hover:-translate-y-1 transition-all duration-300 whitespace-nowrap cursor-pointer shadow-[0_8px_20px_rgba(0,0,0,0.3)] hover:shadow-[0_12px_28px_rgba(0,0,0,0.4)]"
              >
                <span>無料で口座開設する</span>
                <i className="ri-arrow-right-line text-xl md:text-2xl"></i>
              </a>
              <img border="0" width="1" height="1" src="https://www13.a8.net/0.gif?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" alt="" style={{ display: 'none' }} />
            </div>
            <p className="text-white/80 text-xs md:text-sm mt-3 md:mt-4">口座開設・維持費無料 / 最短5分で申込完了</p>
          </div>
        </div>
      </section>

      {/* 記事一覧セクション */}
      <section className="py-12 md:py-16 bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-10">
            <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 mb-2">
              記事一覧
            </h2>
            <p className="text-xs md:text-sm text-gray-600">DMM FX・FX基礎知識に関する記事をまとめています</p>
          </div>

          {/* FX初心者ガイド */}
          <div className="mb-6 md:mb-8">
            <div className="flex items-center justify-between mb-3 md:mb-4">
              <h3 className="text-sm md:text-lg font-bold text-gray-900 flex items-center border-l-4 border-teal-500 pl-2 md:pl-3">
                <i className="ri-user-star-line mr-1.5 md:mr-2 text-teal-600"></i>
                FX初心者ガイド
              </h3>
              <Link to="/fx-guide" className="text-teal-600 text-xs md:text-sm font-semibold hover:text-teal-700 flex items-center cursor-pointer">
                もっと見る <i className="ri-arrow-right-line ml-1"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
              <Link to="/beginner-guide" className="bg-white rounded-lg md:rounded-xl p-4 md:p-5 border border-gray-200 hover:shadow-md hover:border-teal-300 transition-all cursor-pointer group">
                <div className="flex items-center mb-1.5 md:mb-2">
                  <div className="w-7 h-7 md:w-8 md:h-8 bg-teal-100 rounded-lg flex items-center justify-center mr-2 md:mr-3">
                    <i className="ri-user-star-line text-teal-600 text-sm md:text-base"></i>
                  </div>
                  <span className="font-semibold text-sm md:text-base text-gray-900 group-hover:text-teal-600 transition-colors">初心者でも大丈夫？</span>
                </div>
                <p className="text-xs text-gray-500">メリット・デメリットを解説</p>
              </Link>
              <Link to="/fx-basics" className="bg-white rounded-lg md:rounded-xl p-4 md:p-5 border border-gray-200 hover:shadow-md hover:border-teal-300 transition-all cursor-pointer group">
                <div className="flex items-center mb-1.5 md:mb-2">
                  <div className="w-7 h-7 md:w-8 md:h-8 bg-teal-100 rounded-lg flex items-center justify-center mr-2 md:mr-3">
                    <i className="ri-book-open-line text-teal-600 text-sm md:text-base"></i>
                  </div>
                  <span className="font-semibold text-sm md:text-base text-gray-900 group-hover:text-teal-600 transition-colors">FXとは？超入門</span>
                </div>
                <p className="text-xs text-gray-500">仕組みから始め方まで解説</p>
              </Link>
              <Link to="/safety" className="bg-white rounded-lg md:rounded-xl p-4 md:p-5 border border-gray-200 hover:shadow-md hover:border-teal-300 transition-all cursor-pointer group">
                <div className="flex items-center mb-1.5 md:mb-2">
                  <div className="w-7 h-7 md:w-8 md:h-8 bg-teal-100 rounded-lg flex items-center justify-center mr-2 md:mr-3">
                    <i className="ri-shield-check-line text-teal-600 text-sm md:text-base"></i>
                  </div>
                  <span className="font-semibold text-sm md:text-base text-gray-900 group-hover:text-teal-600 transition-colors">FXは危険？</span>
                </div>
                <p className="text-xs text-gray-500">安全性を徹底解説</p>
              </Link>
              <Link to="/minimum-amount" className="bg-white rounded-lg md:rounded-xl p-4 md:p-5 border border-gray-200 hover:shadow-md hover:border-teal-300 transition-all cursor-pointer group">
                <div className="flex items-center mb-1.5 md:mb-2">
                  <div className="w-7 h-7 md:w-8 md:h-8 bg-teal-100 rounded-lg flex items-center justify-center mr-2 md:mr-3">
                    <i className="ri-money-dollar-circle-line text-teal-600 text-sm md:text-base"></i>
                  </div>
                  <span className="font-semibold text-sm md:text-base text-gray-900 group-hover:text-teal-600 transition-colors">少額でいくらから？</span>
                </div>
                <p className="text-xs text-gray-500">最低必要資金を解説</p>
              </Link>
              <Link to="/account-opening" className="bg-white rounded-lg md:rounded-xl p-4 md:p-5 border border-gray-200 hover:shadow-md hover:border-teal-300 transition-all cursor-pointer group">
                <div className="flex items-center mb-1.5 md:mb-2">
                  <div className="w-7 h-7 md:w-8 md:h-8 bg-teal-100 rounded-lg flex items-center justify-center mr-2 md:mr-3">
                    <i className="ri-file-list-3-line text-teal-600 text-sm md:text-base"></i>
                  </div>
                  <span className="font-semibold text-sm md:text-base text-gray-900 group-hover:text-teal-600 transition-colors">口座開設のやり方</span>
                </div>
                <p className="text-xs text-gray-500">スマホで最短5分</p>
              </Link>
              <Link to="/demo-account" className="bg-white rounded-lg md:rounded-xl p-4 md:p-5 border border-gray-200 hover:shadow-md hover:border-teal-300 transition-all cursor-pointer group">
                <div className="flex items-center mb-1.5 md:mb-2">
                  <div className="w-7 h-7 md:w-8 md:h-8 bg-teal-100 rounded-lg flex items-center justify-center mr-2 md:mr-3">
                    <i className="ri-play-circle-line text-teal-600 text-sm md:text-base"></i>
                  </div>
                  <span className="font-semibold text-sm md:text-base text-gray-900 group-hover:text-teal-600 transition-colors">デモ口座は必要？</span>
                </div>
                <p className="text-xs text-gray-500">練習環境の活用法</p>
              </Link>
              <Link to="/app-guide" className="bg-white rounded-lg md:rounded-xl p-4 md:p-5 border border-gray-200 hover:shadow-md hover:border-teal-300 transition-all cursor-pointer group">
                <div className="flex items-center mb-1.5 md:mb-2">
                  <div className="w-7 h-7 md:w-8 md:h-8 bg-teal-100 rounded-lg flex items-center justify-center mr-2 md:mr-3">
                    <i className="ri-smartphone-line text-teal-600 text-sm md:text-base"></i>
                  </div>
                  <span className="font-semibold text-sm md:text-base text-gray-900 group-hover:text-teal-600 transition-colors">アプリの使い方</span>
                </div>
                <p className="text-xs text-gray-500">取引ツールを解説</p>
              </Link>
              <Link to="/how-to-choose" className="bg-white rounded-lg md:rounded-xl p-4 md:p-5 border border-gray-200 hover:shadow-md hover:border-teal-300 transition-all cursor-pointer group">
                <div className="flex items-center mb-1.5 md:mb-2">
                  <div className="w-7 h-7 md:w-8 md:h-8 bg-teal-100 rounded-lg flex items-center justify-center mr-2 md:mr-3">
                    <i className="ri-search-line text-teal-600 text-sm md:text-base"></i>
                  </div>
                  <span className="font-semibold text-sm md:text-base text-gray-900 group-hover:text-teal-600 transition-colors">FX口座の選び方</span>
                </div>
                <p className="text-xs text-gray-500">比較ポイントを解説</p>
              </Link>
            </div>
          </div>

          {/* FX基礎知識 */}
          <div className="mb-6 md:mb-8">
            <div className="flex items-center justify-between mb-3 md:mb-4">
              <h3 className="text-sm md:text-lg font-bold text-gray-900 flex items-center border-l-4 border-amber-500 pl-2 md:pl-3">
                <i className="ri-book-read-line mr-1.5 md:mr-2 text-amber-600"></i>
                FX基礎知識
              </h3>
              <Link to="/fx-basic" className="text-amber-600 text-xs md:text-sm font-semibold hover:text-amber-700 flex items-center cursor-pointer">
                もっと見る <i className="ri-arrow-right-line ml-1"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
              <Link to="/leverage" className="bg-white rounded-lg md:rounded-xl p-4 md:p-5 border border-gray-200 hover:shadow-md hover:border-amber-300 transition-all cursor-pointer group">
                <div className="flex items-center mb-1.5 md:mb-2">
                  <div className="w-7 h-7 md:w-8 md:h-8 bg-amber-100 rounded-lg flex items-center justify-center mr-2 md:mr-3">
                    <i className="ri-scales-3-line text-amber-600 text-sm md:text-base"></i>
                  </div>
                  <span className="font-semibold text-sm md:text-base text-gray-900 group-hover:text-amber-600 transition-colors">レバレッジとは？</span>
                </div>
                <p className="text-xs text-gray-500">仕組みとリスク管理</p>
              </Link>
              <Link to="/spread" className="bg-white rounded-lg md:rounded-xl p-4 md:p-5 border border-gray-200 hover:shadow-md hover:border-amber-300 transition-all cursor-pointer group">
                <div className="flex items-center mb-1.5 md:mb-2">
                  <div className="w-7 h-7 md:w-8 md:h-8 bg-amber-100 rounded-lg flex items-center justify-center mr-2 md:mr-3">
                    <i className="ri-funds-line text-amber-600 text-sm md:text-base"></i>
                  </div>
                  <span className="font-semibold text-sm md:text-base text-gray-900 group-hover:text-amber-600 transition-colors">スプレッドとは？</span>
                </div>
                <p className="text-xs text-gray-500">取引コストを比較</p>
              </Link>
              <Link to="/lot-guide" className="bg-white rounded-lg md:rounded-xl p-4 md:p-5 border border-gray-200 hover:shadow-md hover:border-amber-300 transition-all cursor-pointer group">
                <div className="flex items-center mb-1.5 md:mb-2">
                  <div className="w-7 h-7 md:w-8 md:h-8 bg-amber-100 rounded-lg flex items-center justify-center mr-2 md:mr-3">
                    <i className="ri-stack-line text-amber-600 text-sm md:text-base"></i>
                  </div>
                  <span className="font-semibold text-sm md:text-base text-gray-900 group-hover:text-amber-600 transition-colors">ロットとは？</span>
                </div>
                <p className="text-xs text-gray-500">取引数量の基本</p>
              </Link>
              <Link to="/fx-tax" className="bg-white rounded-lg md:rounded-xl p-4 md:p-5 border border-gray-200 hover:shadow-md hover:border-amber-300 transition-all cursor-pointer group">
                <div className="flex items-center mb-1.5 md:mb-2">
                  <div className="w-7 h-7 md:w-8 md:h-8 bg-amber-100 rounded-lg flex items-center justify-center mr-2 md:mr-3">
                    <i className="ri-money-yen-circle-line text-amber-600 text-sm md:text-base"></i>
                  </div>
                  <span className="font-semibold text-sm md:text-base text-gray-900 group-hover:text-amber-600 transition-colors">税金いくらから？</span>
                </div>
                <p className="text-xs text-gray-500">課税条件と確定申告</p>
              </Link>
            </div>
          </div>

          {/* DMM FX取引ガイド */}
          <div>
            <div className="flex items-center justify-between mb-3 md:mb-4">
              <h3 className="text-sm md:text-lg font-bold text-gray-900 flex items-center border-l-4 border-emerald-500 pl-2 md:pl-3">
                <i className="ri-building-line mr-1.5 md:mr-2 text-emerald-600"></i>
                DMM FX取引ガイド
              </h3>
              <Link to="/fx-trade" className="text-emerald-600 text-xs md:text-sm font-semibold hover:text-emerald-700 flex items-center cursor-pointer">
                もっと見る <i className="ri-arrow-right-line ml-1"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
              <Link to="/reviews" className="bg-white rounded-lg md:rounded-xl p-4 md:p-5 border border-gray-200 hover:shadow-md hover:border-emerald-300 transition-all cursor-pointer group">
                <div className="flex items-center mb-1.5 md:mb-2">
                  <div className="w-7 h-7 md:w-8 md:h-8 bg-emerald-100 rounded-lg flex items-center justify-center mr-2 md:mr-3">
                    <i className="ri-star-line text-emerald-600 text-sm md:text-base"></i>
                  </div>
                  <span className="font-semibold text-sm md:text-base text-gray-900 group-hover:text-emerald-600 transition-colors">評判・口コミ</span>
                </div>
                <p className="text-xs text-gray-500">利用者の声を徹底分析</p>
              </Link>
              <Link to="/campaign" className="bg-white rounded-lg md:rounded-xl p-4 md:p-5 border border-gray-200 hover:shadow-md hover:border-emerald-300 transition-all cursor-pointer group">
                <div className="flex items-center mb-1.5 md:mb-2">
                  <div className="w-7 h-7 md:w-8 md:h-8 bg-emerald-100 rounded-lg flex items-center justify-center mr-2 md:mr-3">
                    <i className="ri-gift-line text-emerald-600 text-sm md:text-base"></i>
                  </div>
                  <span className="font-semibold text-sm md:text-base text-gray-900 group-hover:text-emerald-600 transition-colors">キャンペーン情報</span>
                </div>
                <p className="text-xs text-gray-500">最大50万円キャッシュバック</p>
              </Link>
              <Link to="/support" className="bg-white rounded-lg md:rounded-xl p-4 md:p-5 border border-gray-200 hover:shadow-md hover:border-emerald-300 transition-all cursor-pointer group">
                <div className="flex items-center mb-1.5 md:mb-2">
                  <div className="w-7 h-7 md:w-8 md:h-8 bg-emerald-100 rounded-lg flex items-center justify-center mr-2 md:mr-3">
                    <i className="ri-customer-service-2-line text-emerald-600 text-sm md:text-base"></i>
                  </div>
                  <span className="font-semibold text-sm md:text-base text-gray-900 group-hover:text-emerald-600 transition-colors">サポート情報</span>
                </div>
                <p className="text-xs text-gray-500">24時間サポート体制</p>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
